/**
 * Monaco Editor 0.52+ can throw/reject with "Canceled" when an instance is
 * disposed while async highlighting or model work is still in flight (common
 * with React StrictMode and conditional unmounts). The failure is benign.
 */

export function isMonacoCancellationError(reason) {
  if (reason == null) return false;
  if (reason === 'Canceled') return true;

  const message = typeof reason === 'string' ? reason : reason.message;
  const stringified = typeof reason === 'string' ? reason : String(reason);

  if (
    message === 'Canceled'
    || message === 'Canceled: Canceled'
    || message === 'operation is manually canceled'
    || (typeof message === 'string' && /^Canceled(?:\s|:|$)/.test(message))
    || (typeof message === 'string' && /^Uncaught (?:Error: )?Canceled/.test(message))
  ) {
    return true;
  }

  const name = typeof reason === 'object' ? (reason.name || reason.type) : undefined;
  if (name === 'Canceled' || name === 'cancelation') return true;

  const stack = typeof reason === 'object' ? reason.stack : undefined;
  if (typeof stack === 'string' && stack.includes('monaco-editor')) {
    if (
      message === 'Canceled'
      || name === 'Canceled'
      || stringified === 'Canceled'
      || /Canceled/.test(stringified)
      || stack.includes('cancel@')
    ) {
      return true;
    }
  }

  if (
    typeof reason === 'string'
    && /Canceled/.test(reason)
    && (reason.includes('monaco-editor') || reason.includes('monaco'))
  ) {
    return true;
  }

  return false;
}

function shouldSuppressMonacoCancelEvent(event) {
  if (isMonacoCancellationError(event?.reason)) return true;
  if (isMonacoCancellationError(event?.error)) return true;
  if (isMonacoCancellationError(event?.message)) return true;

  const stack = String(
    event?.error?.stack
    || event?.reason?.stack
    || event?.stack
    || ''
  );
  const combined = [
    event?.message,
    event?.error?.message,
    event?.reason?.message,
    String(event?.error ?? ''),
    String(event?.reason ?? '')
  ]
    .filter(Boolean)
    .join(' ');

  if (/Canceled/.test(combined) && (
    stack.includes('monaco-editor')
    || stack.includes('@monaco-editor/react')
    || combined.includes('monaco-editor')
  )) {
    return true;
  }

  const filename = event?.filename || event?.source;
  if (
    typeof filename === 'string'
    && filename.includes('monaco-editor')
    && /Canceled/.test(String(event?.message || event?.error || ''))
  ) {
    return true;
  }

  return false;
}

function installReactDevOverlayFilter() {
  if (typeof window === 'undefined') return;

  const hook = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
  if (!hook || hook.__flashcodeMonacoCancelFilterInstalled) return;

  hook.__flashcodeMonacoCancelFilterInstalled = true;

  if (typeof hook.onError === 'function') {
    const original = hook.onError;
    hook.onError = (error) => {
      if (isMonacoCancellationError(error)) return;
      original(error);
    };
  }

  if (typeof hook.handleRuntimeError === 'function') {
    const original = hook.handleRuntimeError;
    hook.handleRuntimeError = (error) => {
      if (isMonacoCancellationError(error)) return;
      original(error);
    };
  }
}

/**
 * Install once at app startup (before React render).
 */
export function installMonacoCancellationFilter() {
  if (typeof window === 'undefined' || window.__flashcodeMonacoCancelFilterInstalled) {
    return;
  }

  window.__flashcodeMonacoCancelFilterInstalled = true;

  window.addEventListener(
    'unhandledrejection',
    (event) => {
      if (shouldSuppressMonacoCancelEvent(event)) {
        event.preventDefault();
      }
    },
    true
  );

  window.addEventListener(
    'error',
    (event) => {
      if (shouldSuppressMonacoCancelEvent(event)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  installReactDevOverlayFilter();

  if (typeof window.reportError === 'function') {
    const originalReportError = window.reportError;
    window.reportError = (error) => {
      if (isMonacoCancellationError(error)) return;
      originalReportError(error);
    };
  }

  // CRA/webpack-dev-server overlay also surfaces these via console.error in dev.
  if (process.env.NODE_ENV !== 'production') {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args.some((arg) => isMonacoCancellationError(arg))) {
        return;
      }
      const combined = args.map((arg) => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return `${arg.name}:${arg.message}:${arg.stack || ''}`;
        return String(arg);
      }).join(' ');
      if (combined.includes('Canceled') && (
        combined.includes('monaco-editor')
        || combined.includes('@monaco-editor/react')
      )) {
        return;
      }
      originalConsoleError.apply(console, args);
    };
  }
}
